import React from "react";

import {
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  defineStyleConfig,
  forwardRef,
  ResponsiveValue,
  Box,
} from "@chakra-ui/react";

const buttonSizes = {
  xs: {
    fontSize: "xs",
    py: 1,
    px: 2,
    h: "auto",
    minW: 0,
  },
  sm: {
    fontSize: "sm",
    py: 2.5,
    px: 4,
    h: "auto",
    minW: 0,
  },
  md: {
    fontSize: "md",
    py: 3,
    px: 6,
    h: "auto",
    minW: 0,
  },
  lg: {
    fontSize: "2xl",
    py: 4,
    px: 10,
    h: "auto",
    minW: 0,
  },
};

export const ButtonStyle = defineStyleConfig({
  defaultProps: {
    size: "md",
  },
  sizes: buttonSizes,
});

export type ButtonProps = {
  variant?: "outline" | "solid";
  color?: "primary" | "gray" | "red" | "white" | "disabled";
} & Omit<ChakraButtonProps, "variant" | "color">;

const Button = forwardRef<ButtonProps, "button">(
  (
    { children, variant = "solid", color = "primary", _hover, size = "md", leftIcon, rightIcon, ...buttonProps },
    ref,
  ) => {
    const getIconSpacing = (buttonSize: ResponsiveValue<string>): number => {
      if (typeof buttonSize === "string") {
        return ["xs", "sm"].includes(buttonSize) ? 1 : 2;
      }
      return 2;
    };

    const styleProps = {
      solid: {
        primary: {
          bgColor: "primary",
          color: "#fff",
          borderColor: "primary",
          _hover: {
            bgColor: "rgba(60, 170, 145, 0.80)",
            ..._hover,
          },
        },
        gray: {
          bgColor: "#EDF2F7",
          color: "textBase",
          _hover: {
            bgColor: "rgba(237, 242, 247, 0.80)",
            ..._hover,
          },
        },
        red: {
          bgColor: "#E11F1F",
          color: "#fff",
          _hover: {
            bgColor: "rgba(225, 31, 31, 0.80)",
            ..._hover,
          },
        },
        white: {
          bgColor: "white",
          color: "primary",
        },
        disabled: {
          background: "#6A7573",
          color: "white",
          border: "1px solid",
          borderColor: "transparent",
          _hover: {
            background: "#6A7573",
            color: "white",
            border: "1px solid",
            borderColor: "transparent",
          },
        },
      },
      outline: {
        primary: {
          bgColor: "transparent",
          color: "primary",
          border: "1px solid;",
          borderColor: "primary",
          _hover: {
            bgColor: "primary",
            color: "#ffffff",
            ..._hover,
          },
        },
        red: {
          bgColor: "transparent",
          color: "text.caution.dark",
          border: "1px solid",
          borderColor: "text.caution.dark",
          _hover: {
            bgColor: "#E11F1F",
            color: "#ffffff",
            ..._hover,
          },
        },
      },
    }[variant][color];

    return (
      <ChakraButton
        borderRadius={2}
        whiteSpace="pre-wrap"
        lineHeight="150%"
        fontWeight="bold"
        ref={ref}
        size={size}
        {...styleProps}
        {...buttonProps}
        _disabled={{
          background: "#6A7573",
          color: "white",
          borderColor: "transparent",
        }}
        iconSpacing={getIconSpacing(size)}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
      >
        <Box pt={"1px"}>{children}</Box>
      </ChakraButton>
    );
  },
);

export default Button;
